import { Form, Button } from "react-bootstrap";
import { useRef } from "react";
import { ReactComponent as OutlineCrossSmall } from "assets/svg/outline-cross-small.svg";

import IconButton from "components/icon-button";
import styles from "./styles.module.scss";
import getClasses from "utils/getClasses";
import { useBlockType } from "../../contexts/BlockTypeContext";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";

const getChecked = (option: string, value?: string[] | string) =>
  !value
    ? false
    : typeof value === "string"
    ? value === option
    : Array.isArray(value)
    ? value.includes(option)
    : false;

type Props = {
  className?: string;
  options: string[] | undefined;
  onChangeOptions: (arg: string[]) => void;
  value?: any;
  onChange?: (arg: any) => void;
  /** radio, checkbox or any other icon */
  type: "checkbox" | "radio" | undefined;
  count?: number;
  showIndex?: boolean;
  addProp?: string;
  disabled?: boolean;
};

const Selections = ({
  className,
  options = [],
  onChangeOptions,
  value,
  onChange,
  type,
  count,
  showIndex,
  addProp = "Option",
  disabled,
}: Props) => {
  const { isEdit, isFill } = useBlockType();

  const ref = useRef<HTMLDivElement | null>(null);

  const onRemove = (index: number) => {
    onChangeOptions([...options.slice(0, index), ...options.slice(index + 1)]);
  };

  const onAdd = (val: string) => {
    onChangeOptions([...options, val]);
  };

  const onUpdate = (val: string, index: number) => {
    onChangeOptions([
      ...options.slice(0, index),
      val,
      ...options.slice(index + 1),
    ]);
  };

  return (
    <div
      ref={ref}
      className={getClasses(styles["selections"], className)}
      tabIndex={0}
    >
      {options.map((option, i) => {
        const checked = isEdit ? false : getChecked(option, value);
        return (
          <div
            className={styles["selections__row"]}
            key={i}
            {...(isFill &&
              onChange &&
              !disabled && {
                onClick: () =>
                  onChange(
                    type === "radio"
                      ? option
                      : checked
                      ? (value || []).filter((el) => el !== option)
                      : [...(value || []), option],
                  ),
              })}
          >
            {type && (
              <CustomSelectionControl
                className={styles["selections__control"]}
                type={type || "radio"}
                checked={checked}
              />
            )}
            <div className={styles["selections__edit"]}>
              {showIndex && (
                <div className={styles["selections__index"]}>{`${i + 1}.`}</div>
              )}
              <Form.Control
                {...(!isEdit && {
                  readOnly: true,
                })}
                className={styles["selections__input"]}
                value={option || ""}
                onChange={(e) => {
                  onUpdate && onUpdate(e.target.value, i);
                }}
              />
              {isEdit && (
                <IconButton
                  className={styles["selections__remove"]}
                  onClick={() => {
                    if (ref.current) {
                      ref.current.focus();
                    }
                    onRemove && onRemove(i);
                  }}
                >
                  <OutlineCrossSmall />
                </IconButton>
              )}
            </div>
          </div>
        );
      })}

      {isEdit && (
        <div className={styles["selections__row"]}>
          {!type ? null : (
            <CustomSelectionControl
              className={styles["selections__control"]}
              type={type}
              checked={false}
            />
          )}
          <Button
            className={styles["selections__add"]}
            variant={"link"}
            onClick={() => onAdd && onAdd(`${addProp} ${options.length + 1}`)}
            {...(count !== undefined && {
              disabled: options.length >= (count || 0),
            })}
          >
            {`+ Add ${addProp}`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Selections;
